import React from 'react';
import { Spin, Table, Dropdown, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageOutlined, MoreOutlined, ProjectOutlined } from '@ant-design/icons';
import StatusBadge from '@/components/ui/StatusBadge';
import LeadStateSwitch from '@/components/ui/LeadStateSwitch';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@stores/auth.store';
import { useOpenMarketLeadDetail, OPEN_MARKET_LEAD_DETAIL_QUERY_KEY } from '@hooks/open-market/useOpenMarketLeads';
import { createProjectRequest } from '@services/open-market.service';
import { PROJECT_REQUESTS_QUERY_KEY } from '@hooks/open-market/useProjectRequests';
import { Container } from '@/components/ui/Container';
import {
    DetailTitleSmall,
    MetaRowInline,
    CategoryPill,
    ContentCard,
    FieldLabelSmall,
    DetailValue,
    ContentCardTitle,
    GridTwoCol,
    ThemedAntTableWrap,
    ActionsWrap,
    ButtonWrap,
    DetailPanelLoading,
    DetailValueError,
    EmptyState,
    TextLineClampTwo,
} from './style';

const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return `$${num.toLocaleString()}`;
};

const capFirst = (s) => (s && s.length ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—');

const OpenMarketLeadDetailPanel = ({
    leadId,
    myBid: myBidFromList,
    onPlaceBid,
    onApproveBid,
    approveBidPending,
    onDeclineBid,
    declineBidPending,
    onStartChat,
    onStateChange,
    stateChangePending,
    stateChangingLeadId,
}) => {
    const user = useAuthStore((s) => s.user);
    const { data: lead, isLoading, isError } = useOpenMarketLeadDetail(leadId);

    const isClientOfLead = !!lead && !!user?.id && String(user.id) === String(lead.clientUserId);
    const role = useAuthStore((s) => s.role);
    const isClientProfile =
        user?.isClient === true ||
        (typeof role === 'string' && role.toLowerCase() === 'client') ||
        (typeof user?.role === 'string' && user.role.toLowerCase() === 'client') ||
        (Array.isArray(user?.roles) && user.roles.some((r) => String(r).toLowerCase() === 'client'));
    // Use bids from lead detail (GET /leads/:id returns bids); no separate bids API call.
    const bids = Array.isArray(lead?.bids) ? lead.bids : [];
    const acceptedBid = bids.find((b) => (b.status || '').toLowerCase() === 'accepted');
    const inProgress = (lead?.status || '').toLowerCase() === 'in_progress';
    const myBid =
        myBidFromList ??
        (bids.length && user?.id ? (bids.find((b) => String(b.submittedById) === String(user.id)) ?? null) : null);

    const queryClient = useQueryClient();
    const createProjectRequestMutation = useMutation({
        mutationFn: ({ openMarketLeadId, openMarketBidId }) =>
            createProjectRequest({ openMarketLeadId, openMarketBidId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: OPEN_MARKET_LEAD_DETAIL_QUERY_KEY(leadId) });
            queryClient.invalidateQueries({ queryKey: PROJECT_REQUESTS_QUERY_KEY });
            message.success('Project request created');
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || err?.message || 'Failed to create project request');
        },
    });

    const handleCreateProject = (bid) => {
        if (!leadId || !bid?.id) return;
        createProjectRequestMutation.mutate({ openMarketLeadId: leadId, openMarketBidId: bid.id });
    };

    if (!leadId) return null;

    if (isLoading) {
        return (
            <DetailPanelLoading>
                <Spin size="large" />
            </DetailPanelLoading>
        );
    }

    if (isError || !lead) {
        return (
            <Container $display="flex" $flexDirection="column" $gap="20px" $padding="0 4px">
                <DetailValueError>Failed to load lead details.</DetailValueError>
            </Container>
        );
    }

    const bidColumns = [
        {
            title: 'Tenant / Amount',
            key: 'tenantAmount',
            width: 160,
            render: (_, bid) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{bid?.tenant?.name ?? '—'}</span>
                    <span style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>
                        {formatMoney(bid?.amount)}
                    </span>
                </div>
            ),
        },
        {
            title: 'Proposal',
            dataIndex: 'proposal',
            key: 'proposal',
            width: 320,
            render: (v) => <TextLineClampTwo title={v || ''}>{v ?? '—'}</TextLineClampTwo>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 110,
            render: (status) => <StatusBadge status={status} />,
        },
        {
            title: 'Submitted',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 130,
            render: (v) =>
                v
                    ? new Date(v).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                      })
                    : '—',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 180,
            render: (_, bid) => {
                const statusLower = (bid.status || '').toLowerCase();
                const isPending = statusLower === 'pending';
                const isAccepted = statusLower === 'accepted';
                if (isClientOfLead && isPending) {
                    return (
                        <ActionsWrap>
                            <Button
                                $primary
                                $blackOnLight
                                $size="12px"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onApproveBid?.(bid.id, leadId);
                                }}
                                loading={approveBidPending}
                                disabled={approveBidPending || declineBidPending}
                            >
                                Interested
                            </Button>
                            <Button
                                type="button"
                                $primary={false}
                                $size="12px"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDeclineBid?.(bid.id, leadId);
                                }}
                                loading={declineBidPending}
                                disabled={approveBidPending || declineBidPending}
                            >
                                Reject
                            </Button>
                        </ActionsWrap>
                    );
                }
                if (isClientProfile) {
                    const menuItems = [
                        {
                            key: 'chat',
                            icon: <MessageOutlined />,
                            label: 'Chat',
                            onClick: () => onStartChat?.(bid),
                        },
                        ...(isAccepted
                            ? [
                                  {
                                      key: 'project',
                                      icon: <ProjectOutlined />,
                                      label: 'Project Request',
                                      onClick: () => handleCreateProject(bid),
                                      disabled: createProjectRequestMutation.isPending,
                                  },
                              ]
                            : []),
                    ];
                    return (
                        <span onClick={(e) => e.stopPropagation()}>
                            <Dropdown
                                menu={{ items: menuItems }}
                                trigger={['click']}
                                placement="bottomRight"
                                overlayStyle={{ minWidth: 220 }}
                                dropdownRender={(menu) => <div style={{ minWidth: 220 }}>{menu}</div>}
                            >
                                <Button
                                    type="button"
                                    $primary={false}
                                    $size="12px"
                                    icon={<MoreOutlined />}
                                    loading={createProjectRequestMutation.isPending}
                                />
                            </Dropdown>
                        </span>
                    );
                }
                return '—';
            },
        },
    ];

    return (
        <Container $display="flex" $flexDirection="column" $gap="20px" $padding="0 4px">
            <ContentCard $padding="20px" $marginBottom="0">
                <DetailTitleSmall>{lead.title ?? '—'}</DetailTitleSmall>
                <MetaRowInline>
                    <StatusBadge status={lead.status} />
                    <CategoryPill>{capFirst(lead.category ?? '—')}</CategoryPill>
                    {lead.createdAt && (
                        <CategoryPill>
                            {new Date(lead.createdAt).toLocaleDateString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                            })}
                        </CategoryPill>
                    )}
                </MetaRowInline>
            </ContentCard>

            <ContentCard $padding="18px 20px">
                <FieldLabelSmall>Description</FieldLabelSmall>
                <DetailValue as="div">{lead.description ?? '—'}</DetailValue>
                <GridTwoCol>
                    <div>
                        <FieldLabelSmall>Budget range</FieldLabelSmall>
                        <DetailValue as="div">{`${formatMoney(lead.budgetMin)} – ${formatMoney(lead.budgetMax)}`}</DetailValue>
                    </div>
                    <div>
                        <FieldLabelSmall>Lead status</FieldLabelSmall>
                        <DetailValue as="div">
                            <StatusBadge status={lead.status} />
                        </DetailValue>
                    </div>
                </GridTwoCol>
                {isClientOfLead && onStateChange && (
                    <div style={{ marginTop: 16 }}>
                        <FieldLabelSmall>State</FieldLabelSmall>
                        <DetailValue as="div" style={{ marginTop: 6 }}>
                            <LeadStateSwitch
                                value={lead.state || 'paused'}
                                onChange={() => onStateChange(leadId, lead.state)}
                                disabled={stateChangePending}
                                loading={stateChangePending && stateChangingLeadId === leadId}
                            />
                        </DetailValue>
                    </div>
                )}
            </ContentCard>

            <>
                <ContentCardTitle $marginBottom={12}>
                    {isClientProfile ? `Bids (${bids.length})` : 'My Bids'}
                </ContentCardTitle>
                {bids.length > 0 ? (
                    <ThemedAntTableWrap>
                        <Table
                            columns={bidColumns}
                            dataSource={bids.map((b) => ({ ...b, key: b.id }))}
                            pagination={false}
                            size="small"
                            scroll={{ x: 920 }}
                        />
                    </ThemedAntTableWrap>
                ) : (
                    <EmptyState style={{ padding: 16 }}>No bids yet.</EmptyState>
                )}
            </>

            {onPlaceBid && !myBid && (
                <ButtonWrap>
                    <Button $primary $blackOnLight $size="14px" onClick={() => onPlaceBid(leadId)}>
                        Place Bid
                    </Button>
                </ButtonWrap>
            )}
        </Container>
    );
};

export default OpenMarketLeadDetailPanel;
